import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import React, { useState } from "react";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { getCurrentTenantSlug, getTenantApiUrl } from '../../tenantConfig';
import "../toastr/custom_toastr.css";
import "./ContactForm.scss";

// Configure toastr options globally
toastr.options = {
  closeButton: true,
  progressBar: true,
  positionClass: "toast-top-right",
  showMethod: "fadeIn",
  hideMethod: "fadeOut",
};

const initialFormState = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

const InputField = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  onBlur,
  required,
  errors,
  touched,
  ...rest
}) => (
  <div
    className={`form-group mb-3 ${
      touched[name] && errors[name] ? "has-error" : ""
    } ${touched[name] && !errors[name] && value.trim() ? "has-success" : ""}`}
  >
    <label htmlFor={name} className="form-label">
      {label} {required && <span className="text-danger">*</span>}
    </label>
    <input
      type={type}
      className="form-control"
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      required={required}
      {...rest}
    />
    {touched[name] && errors[name] && (
      <div
        className="text-danger small mt-1"
        style={{ fontSize: "0.75rem", fontWeight: "500" }}
      >
        {errors[name]}
      </div>
    )}
  </div>
);

const TextAreaField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  required,
  rows = 4,
  errors,
  touched,
  ...rest
}) => (
  <div
    className={`form-group mb-3 ${
      touched[name] && errors[name] ? "has-error" : ""
    } ${touched[name] && !errors[name] && value.trim() ? "has-success" : ""}`}
  >
    <label htmlFor={name} className="form-label">
      {label} {required && <span className="text-danger">*</span>}
    </label>
    <textarea
      className="form-control"
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      required={required}
      rows={rows}
      {...rest}
    />
    {touched[name] && errors[name] && (
      <div
        className="text-danger small mt-1"
        style={{ fontSize: "0.75rem", fontWeight: "500" }}
      >
        {errors[name]}
      </div>
    )}
  </div>
);

const ContactForm = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const fieldErrors = validateField(name, formData[name]);
    setErrors((prev) => ({
      ...prev,
      [name]: fieldErrors[name] || "",
    }));
  };

  const validateField = (fieldName, value) => {
    const fieldErrors = {};

    switch (fieldName) {
      case "name":
        if (!value.trim()) {
          fieldErrors[fieldName] = "Name is required";
        }
        break;
      case "email":
        if (!value.trim()) {
          fieldErrors[fieldName] = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          fieldErrors[fieldName] =
            "Please enter a valid email address";
        }
        break;
      case "message":
        if (!value.trim()) {
          fieldErrors[fieldName] = "Message is required";
        } else if (value.trim().length < 10) {
          fieldErrors[fieldName] =
            "Message must be at least 10 characters long";
        }
        break;
      default:
        break;
    }

    return fieldErrors;
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((field) => {
      const fieldError = validateField(field, formData[field]);
      if (fieldError[field]) {
        newErrors[field] = fieldError[field];
      }
    });
    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((error) => {
        toastr.error(error);
      });
      return;
    }

    setLoading(true);

    try {
      const tenantSlug = getCurrentTenantSlug();
      const apiBaseUrl = getTenantApiUrl(tenantSlug);
      const url = `${apiBaseUrl}/tenant/contact/submit/`;

      const response = await axios.post(url, formData);

      if (response.data && response.data.success) {
        // toastr.success(
        //   response.data.message ||
        //     "Thank you for your message! We'll get back to you soon."
        // );
        setFormData(initialFormState);
        setErrors({});
        setTouched({});
        setIsSubmitted(true);
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        toastr.error(
          response.data.message ||
            "Failed to send message. Please try again."
        );
      }
    } catch (error) {
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          toastr.error(error.response.data.message);
        } else if (error.response.status === 404) {
          toastr.error(
            "Contact API endpoint not found. Please check your configuration."
          );
          toastr.info(
            "Note: This is a demo. In production, your message would be sent to the server."
          );
          setFormData(initialFormState);
        } else if (error.response.status === 500) {
          toastr.error("Server error. Please try again later.");
        } else {
          toastr.error(`Request failed with status ${error.response.status}`);
        }
      } else if (error.request) {
        toastr.error(
          "No response from server. Please check your internet connection."
        );
        toastr.info(
          "Note: This is a demo. In production, your message would be sent to the server."
        );
        setFormData(initialFormState);
      } else {
        toastr.error("Failed to send message. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-form-container">
      <div className="contact-form-wrapper">
        <div className="form-header text-center mb-3">
          <h3 className="form-title">Get In Touch</h3>
          <p className="form-subtitle">
            Have questions? We're here to help!
          </p>
        </div>

        <form onSubmit={handleSubmit} className="contact-form">
          {isSubmitted && (
            <div
              className="alert alert-success text-center mb-3"
              role="alert"
            >
              <i className="fas fa-check-circle me-2"></i>
              Thank you! Your message has been sent successfully. We'll
              get back to you soon.
            </div>
          )}

          <div className="row g-2">
            <div className="col-md-6">
              <InputField
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
                touched={touched}
                required
                placeholder="Enter your full name"
              />
            </div>
            <div className="col-md-6">
              <InputField
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
                touched={touched}
                required
                placeholder="Enter your email address"
              />
            </div>
          </div>

          <InputField
            label="Phone Number"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Enter your phone number (optional)"
          />

          <TextAreaField
            label="Message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            required
            rows={3}
            placeholder="Tell us how we can help you..."
          />

          <div className="text-center mt-3">
            <button
              type="submit"
              className="btn btn-primary submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Sending...
                </>
              ) : (
                "Send Message"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
